/**
 * Dia civil de São Paulo — a ÚNICA fonte de "hoje", "que dia foi isto" e
 * "onde começa e termina o dia" nos apps (IR-TZ-006, dono 21/10/2026).
 *
 * POR QUE EXISTE: o clube opera em Brasília, mas o navegador pode estar em
 * qualquer fuso (tablet da recepção em GMT 0) e `toISOString()` é sempre UTC.
 * Com isso, entre 21h e meia-noite "hoje" virava amanhã, a reserva caía no dia
 * seguinte e filtro por dia contava a venda das 22h no dia errado. Antes desta
 * regra havia mais de dez cópias da conta, cada uma de um jeito.
 *
 * CONVENÇÕES:
 * - "Dia" é sempre a string 'yyyy-MM-dd' (dia CIVIL, sem fuso). "Mês" é 'yyyy-MM'.
 * - Instante é `Date` ou string ISO com fuso.
 * - Nada aqui lê `getHours/getDate/setHours` do `Date` (fuso do navegador):
 *   o dia sai do `Intl` com `timeZone: 'America/Sao_Paulo'` e a aritmética de
 *   dias é feita em UTC puro (`Date.UTC`), onde não há fuso nenhum.
 * - Bordas de período para coluna `timestamptz`: `gte` = meia-noite de SP do
 *   primeiro dia e `lt` = meia-noite de SP do dia SEGUINTE ao último (fim
 *   exclusivo — pega 23:59:59.999). Coluna `date` NÃO precisa de borda: o dia
 *   'yyyy-MM-dd' já é o valor certo.
 * - O offset vem do tzdata (`Intl`), não é '-03:00' fixo: um pagamento de 2018
 *   (horário de verão) cai no mesmo dia que o `AT TIME ZONE` do Postgres diz.
 */

export const FUSO_SP = 'America/Sao_Paulo';

const DIA_RE = /^\d{4}-\d{2}-\d{2}$/;

const fmtDia = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO_SP,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function paraDate(instante: Date | string | number): Date {
  return instante instanceof Date ? instante : new Date(instante);
}

/** Dia de São Paulo ('yyyy-MM-dd') em que o instante aconteceu. */
export function diaSP(instante: Date | string | number): string {
  return fmtDia.format(paraDate(instante));
}

/** Hoje em São Paulo ('yyyy-MM-dd'). `agora` é injetável para teste. */
export function hojeSP(agora: Date = new Date()): string {
  return diaSP(agora);
}

/** Mês de São Paulo ('yyyy-MM') em que o instante aconteceu. */
export function mesSP(instante: Date | string | number): string {
  return diaSP(instante).slice(0, 7);
}

/** É um dia 'yyyy-MM-dd' válido do calendário? */
export function ehDia(dia: string): boolean {
  if (!DIA_RE.test(dia)) return false;
  const [a, m, d] = dia.split('-').map(Number);
  const dt = new Date(Date.UTC(a, m - 1, d));
  return dt.getUTCFullYear() === a && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function partes(dia: string): [number, number, number] {
  if (!ehDia(dia)) throw new RangeError(`dia inválido: ${dia}`);
  const [a, m, d] = dia.split('-').map(Number);
  return [a, m, d];
}

const doUTC = (dt: Date) => dt.toISOString().slice(0, 10);

/** Soma `n` dias de CALENDÁRIO (n pode ser negativo). */
export function somarDias(dia: string, n: number): string {
  const [a, m, d] = partes(dia);
  return doUTC(new Date(Date.UTC(a, m - 1, d + n)));
}

/** Soma `n` meses, prendendo no último dia do mês (31/01 + 1 = 28 ou 29/02). */
export function somarMeses(dia: string, n: number): string {
  const [a, m, d] = partes(dia);
  const ultimo = new Date(Date.UTC(a, m - 1 + n + 1, 0)).getUTCDate();
  return doUTC(new Date(Date.UTC(a, m - 1 + n, Math.min(d, ultimo))));
}

/** Primeiro dia do mês do dia dado. */
export function inicioDoMes(dia: string): string {
  return `${dia.slice(0, 7)}-01`;
}

/** Último dia do mês do dia dado. */
export function fimDoMes(dia: string): string {
  const [a, m] = partes(dia);
  return doUTC(new Date(Date.UTC(a, m, 0)));
}

/** Dia da semana do dia civil: 0 = domingo … 6 = sábado (sem fuso no meio). */
export function diaDaSemana(dia: string): number {
  const [a, m, d] = partes(dia);
  return new Date(Date.UTC(a, m - 1, d)).getUTCDay();
}

/** Diferença em dias de calendário (b − a). */
export function diasEntre(a: string, b: string): number {
  const [a1, m1, d1] = partes(a);
  const [a2, m2, d2] = partes(b);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86_400_000);
}

/**
 * Offset de São Paulo naquele dia ('-03:00'; '-02:00' no verão antigo).
 *
 * `timeZoneName: 'longOffset'` não existe em WebView antigo (iOS < 15.4,
 * Chrome/Android < 95) e lá o CONSTRUTOR lança RangeError — a tela de reserva
 * cairia. O caminho principal é por partes numéricas (suportado desde sempre):
 * a hora de SP do meio-dia UTC daquele dia, menos 12.
 */
export function offsetSP(dia: string): string {
  partes(dia);
  try {
    const p = fmtHora.formatToParts(new Date(`${dia}T12:00:00Z`));
    const num = (t: string) => Number(p.find((x) => x.type === t)?.value);
    const minutos = ((num('hour') % 24) * 60 + num('minute')) - 12 * 60;
    if (!Number.isFinite(minutos)) return '-03:00';
    const sinal = minutos < 0 ? '-' : '+';
    const abs = Math.abs(minutos);
    return `${sinal}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
  } catch {
    return '-03:00';
  }
}

const fmtHora = new Intl.DateTimeFormat('en-US', {
  timeZone: FUSO_SP,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Instante (ISO UTC) da meia-noite de São Paulo do dia. */
export function meiaNoiteSP(dia: string): string {
  return new Date(`${dia}T00:00:00${offsetSP(dia)}`).toISOString();
}

/** Instante (ISO UTC) de um horário 'HH:mm' de São Paulo naquele dia. */
export function instanteSP(dia: string, hhmm: string): string {
  if (!/^\d{2}:\d{2}$/.test(hhmm)) throw new RangeError(`hora inválida: ${hhmm}`);
  return new Date(`${dia}T${hhmm}:00${offsetSP(dia)}`).toISOString();
}

export interface Bordas {
  /** Inclusivo: `.gte(col, gte)`. */
  gte: string;
  /** Exclusivo: `.lt(col, lt)`. */
  lt: string;
}

/** Bordas do dia de SP para filtrar coluna `timestamptz`. */
export function limitesDoDiaSP(dia: string): Bordas {
  return { gte: meiaNoiteSP(dia), lt: meiaNoiteSP(somarDias(dia, 1)) };
}

/** Bordas do período [de, ate] (dias inclusivos) para coluna `timestamptz`. */
export function limitesDoPeriodoSP(de: string, ate: string): Bordas {
  return { gte: meiaNoiteSP(de), lt: meiaNoiteSP(somarDias(ate, 1)) };
}

/**
 * Lê 'yyyy-MM-dd' para EXIBIR com date-fns/Intl sem voltar um dia:
 * `new Date('2026-11-10')` é meia-noite UTC = 21h do dia 9 em Brasília. Aqui
 * vira meio-dia UTC, que é o mesmo dia civil em qualquer fuso de −11h a +11h.
 */
export function dataParaExibir(dia: string): Date {
  const [a, m, d] = partes(dia);
  return new Date(Date.UTC(a, m - 1, d, 12));
}

const fmtBR = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'UTC',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** 'yyyy-MM-dd' → 'dd/MM/yyyy', sem fuso no meio. */
export function diaBR(dia: string): string {
  return fmtBR.format(dataParaExibir(dia));
}
